import type React from 'react';
import { cloneElement, useCallback, useEffect, useState } from 'react';

import { useMutation } from '@edreamsodigeo/frontend-api-client';
import { useFrontendApiBlockingError } from '@edreamsodigeo/frontend-core';

import { useGetBuyerFromURL } from 'clientState/hooks/useGetBuyerFromURL';

import { ADD_BUYER_TO_SHOPPING_BASKET } from './operations/PostBookingBuyerProvider.mutations';

import type {
  Ap_AddContactDetailsToShoppingBasketMutation,
  Ap_AddContactDetailsToShoppingBasketMutationVariables,
} from '#src/generated/graphql';
import type { ErrorType } from '#src/utils/types';

const ADD_BUYER_TO_SHOPPING_BASKET_ERROR_CODE =
  'ADD_BUYER_TO_SHOPPING_BASKET_ERROR_CODE';

export const PostBookingBuyerProvider = ({
  children,
  onError = () => null,
  shoppingBasketId,
}: {
  children: React.ReactNode;
  shoppingBasketId: string;
  onError?: (
    errors: ErrorType[] | undefined | null,
    errorIdentifier: string,
  ) => void;
}) => {
  const [buyerLoaded, setBuyerLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const onSuccess = useCallback(() => setLoading(false), []);

  const addContactDetailsToShoppingBasketResponse = useMutation<
    Ap_AddContactDetailsToShoppingBasketMutation,
    Ap_AddContactDetailsToShoppingBasketMutationVariables
  >(ADD_BUYER_TO_SHOPPING_BASKET, {
    onError: ({ errors }) => {
      onError?.(errors, ADD_BUYER_TO_SHOPPING_BASKET_ERROR_CODE);
    },
    onSuccess,
  });

  useFrontendApiBlockingError(addContactDetailsToShoppingBasketResponse);
  const { mutate } = addContactDetailsToShoppingBasketResponse;

  const { buyer, loading: buyerLoading } = useGetBuyerFromURL();

  useEffect(() => {
    if (!buyerLoading && !buyerLoaded) {
      mutate({
        request: {
          address: buyer?.address,
          cityName: buyer?.cityName,
          country: buyer?.country,
          countryCode: buyer?.phoneNumber?.countryCode,
          countryPhoneNumber: buyer?.phoneNumber?.countryCode,
          email: buyer?.mail,
          lastName: buyer?.lastNames,
          name: buyer?.name,
          phoneNumber: buyer?.phoneNumber?.number,
          postcode: buyer?.zipCode,
          shoppingId: shoppingBasketId,
          typePhoneNumber: buyer?.phoneNumber?.type,
        },
      });
      setBuyerLoaded(true);
    }
  }, [buyerLoading, buyerLoaded, mutate, shoppingBasketId, buyer]);

  return loading
    ? null
    : cloneElement(children as React.ReactElement, { shoppingBasketId });
};
